import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { SabiasQueNY, estiloH3NY, estiloPNY } from "./nyHelpers";

// Template77: Día 3 DUMBO Brooklyn (Page3). Contenido desde page.
const Template77 = ({
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItems = (html) => {
		if (!html) return [];
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const ps = doc.querySelectorAll("p");
		const items = [];
		ps.forEach((p) => {
			const text = (p.textContent ?? p.innerText ?? "").trim();
			if (!text) return;
			const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
			if (emojiMatch)
				items.push({ icono: emojiMatch[1], texto: emojiMatch[2].trim() });
		});
		return items;
	};

	const title = page?.texts?.[0]?.content;
	const intro = page?.texts?.[1]?.content;
	const vistaTitle = page?.texts?.[2]?.content;
	const vistaBody = page?.texts?.[3]?.content;
	const consejoText = page?.texts?.[4]?.content;
	const pebbleTitle = page?.texts?.[5]?.content;
	const pebbleBody = page?.texts?.[6]?.content;
	const janeTitle = page?.texts?.[7]?.content;
	const janeBody = page?.texts?.[8]?.content;
	const sabiasQueItems = parseSabiasQueItems(page?.texts?.[9]?.content);
	const comoLlegarTitle = page?.texts?.[10]?.content;
	const comoLlegarLines = page?.texts?.[11]?.content;
	const yellowJane = page?.texts?.[12]?.content;
	const heroSubtitle = page?.texts?.[13]?.content;

	const comoLlegarItems = comoLlegarLines
		? String(comoLlegarLines)
				.split(/\n/)
				.map((s) => s.trim())
				.filter(Boolean)
		: [];

	const dumboSrc = getImageSrc("dumbo");
	const dumboPortadaSrc = getImageSrc("dumbo-portada");
	const badgeSrc = getImageSrc("badge");
	const camaraSrc = getImageSrc("camara");
	const pebbleSrc = getImageSrc("pebble");
	const janeSrc = getImageSrc("jane");
	const logoSrc = getCanvasImageUrl("logo");

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				backgroundColor: "#f7f7f7",
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
				"& p": { marginTop: "0px !important", marginBottom: "0px !important" },
				"& a": {
					color: "#007a8d",
					fontWeight: 700,
					textDecoration: "underline",
				},
			}}
		>
			<div
				data-block-id="header"
				style={{
					backgroundColor: headerBackgroundColor || "#59c3d8",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: fontFamily || '"Poppins", sans-serif',
					fontSize: "14px",
					fontWeight: 700,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 9999,
				}}
			>
				{title}
				{logoSrc && (
					<img
						src={logoSrc}
						alt="logo"
						style={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</div>

			{dumboSrc && (
				<div data-block-id="image-0" style={{ position: "relative" }}>
					<img
						src={dumboSrc}
						alt="Washington Street"
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
					{dumboPortadaSrc && (
						<img
							src={dumboPortadaSrc}
							alt="dumbo"
							style={{ width: 100, position: "absolute", bottom: 55, left: 21 }}
						/>
					)}
					{badgeSrc && (
						<img
							src={badgeSrc}
							alt="dumbo"
							style={{
								width: 120,
								position: "absolute",
								bottom: -40,
								right: -60,
							}}
						/>
					)}
					{heroSubtitle && (
						<h1
							style={{
								fontFamily: '"Poppins", sans-serif',
								fontSize: 32,
								textAlign: "center",
								position: "absolute",
								bottom: "0",
								margin: 10,
								left: 20,
								color: "#fff",
								textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
							}}
						>
							{heroSubtitle}
						</h1>
					)}
				</div>
			)}

			<div style={{ padding: "10px 12px" }}>
				{intro && (
					<div data-block-id="text-1">
						<p
							style={{
								fontFamily: '"Poppins", sans-serif',
								fontSize: 13,
								color: "#333",
								margin: 0,
								padding: 0,
							}}
						>
							{intro}
						</p>
					</div>
				)}

				<div
					style={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						gap: 16,
						position: "relative",
					}}
				>
					{/* Vista famosa */}
					{(vistaTitle || vistaBody) && (
						<div data-block-id="text-2" style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
							<div>
								{vistaTitle && (
									<h3 style={{ ...estiloH3NY, marginBottom: 8, marginTop: 8 }}>
										{vistaTitle}
									</h3>
								)}
								{vistaBody && (
									<p style={{ ...estiloPNY, color: "#444" }}>{vistaBody}</p>
								)}
							</div>
							<div style={{ position: "relative" }}>
								{camaraSrc && (
									<img
										src={camaraSrc}
										alt="Camera"
										style={{
											width: 45,
											position: "absolute",
											top: isSmallScreen ? -20 : -30,
											left: "50%",
											transform: "translateX(-50%)",
										}}
									/>
								)}
								{consejoText && (
									<div
										style={{
											backgroundColor: "#59c3d8",
											border: "6px solid #377885",
											padding: 16,
											fontSize: 9,
											borderRadius: "50%",
											width: 130,
											height: 130,
											color: "white",
											textAlign: "center",
											display: "flex",
											justifyContent: "center",
											flexDirection: "column",
											alignItems: "center",
											fontFamily: '"Poppins", sans-serif',
											marginTop: isSmallScreen ? 12 : 0,
										}}
									>
										<strong style={{ fontSize: 9 }}>CONSEJO:</strong>
										{consejoText}
									</div>
								)}
							</div>
						</div>
					)}

					{/* Pebble Beach */}
					{(pebbleTitle || pebbleBody || pebbleSrc) && (
						<div
							data-block-id="text-3"
							style={{
								display: "flex",
								gap: 20,
								alignItems: "flex-start",
								marginTop: isSmallScreen ? 0 : -15,
							}}
						>
							{pebbleSrc && (
								<img
									src={pebbleSrc}
									alt="Pebble Beach"
									style={{ width: "40%", borderRadius: 12, objectFit: "cover" }}
								/>
							)}
							<div style={{ width: "60%", position: "relative" }}>
								{sabiasQueItems[0] && (
									<div style={{ position: "absolute", top: -35, right: "65%" }}>
										<SabiasQueNY
											icono={sabiasQueItems[0].icono}
											texto={sabiasQueItems[0].texto}
										/>
									</div>
								)}
								{pebbleTitle && (
									<h3 style={{ ...estiloH3NY, marginBottom: 4, marginTop: 0 }}>
										{pebbleTitle}
									</h3>
								)}
								{pebbleBody && (
									<p
										style={{
											fontSize: 13,
											color: "#444",
											fontFamily: '"Poppins", sans-serif',
											margin: 0,
										}}
									>
										{pebbleBody}
									</p>
								)}
							</div>
						</div>
					)}

					{/* Jane's Carousel */}
					{(janeTitle || janeBody) && (
						<div data-block-id="text-4">
							{janeTitle && (
								<h3
									style={{
										...estiloH3NY,
										marginBottom: 4,
										marginTop: isSmallScreen ? 0 : 4,
									}}
								>
									{janeTitle}
								</h3>
							)}
							{janeBody && (
								<p
									style={{ fontSize: 13, fontFamily: '"Poppins", sans-serif' }}
								>
									{janeBody}
								</p>
							)}
						</div>
					)}

					{/* Cómo llegar */}
					{(comoLlegarTitle || comoLlegarItems.length > 0) && (
						<div
							data-block-id="text-5"
							style={{
								backgroundColor: "#e0f7fa",
								border: "3px solid #59c3d8",
								borderRadius: 12,
								padding: "16px 20px",
								fontSize: 12,
								fontFamily: '"Poppins", sans-serif',
								color: "#333",
								width: isSmallScreen ? "60%" : "100%",
								marginTop: -10,
							}}
						>
							{comoLlegarTitle && (
								<h4
									style={{
										margin: 0,
										marginBottom: 8,
										fontSize: 13,
										fontWeight: 700,
										display: "flex",
										alignItems: "center",
										gap: 6,
										color: "#007a8d",
										textTransform: "uppercase",
									}}
								>
									{comoLlegarTitle}
								</h4>
							)}
							{comoLlegarItems.length > 0 && (
								<ul style={{ paddingLeft: 20, margin: 0, lineHeight: 1.5 }}>
									{comoLlegarItems.map((line, i) => (
										<li key={i}>
											{line.includes(" hasta ") ? (
												<>
													{"Línea "}
													<strong>
														{line.split(" hasta ")[0].replace("Línea ", "")}
													</strong>
													{" hasta "}
													<em>
														{line.split(" hasta ").slice(1).join(" hasta ")}
													</em>
												</>
											) : (
												line
											)}
										</li>
									))}
								</ul>
							)}
						</div>
					)}

					{janeSrc && (
						<img
							src={janeSrc}
							alt="Jane"
							style={{
								width: 100,
								position: "absolute",
								bottom: 0,
								right: isSmallScreen ? -30 : 0,
							}}
						/>
					)}
					{yellowJane && (
						<div
							style={{
								position: "absolute",
								bottom: isSmallScreen ? 90 : 70,
								right: isSmallScreen ? 0 : 30,
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 0px 16px",
								padding: "10px 12px",
								fontSize: 11,
								fontFamily: '"Poppins", sans-serif',
								color: "#444",
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: 160,
							}}
						>
							{yellowJane.includes(".") ? (
								<>
									<strong>{yellowJane.split(".")[0]}.</strong>
									<br />
									{yellowJane.split(".").slice(1).join(".").trim()}
								</>
							) : (
								yellowJane
							)}
						</div>
					)}
				</div>
			</div>
		</Box>
	);
};

export default Template77;
